import { useState, useEffect, useRef } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Download, RefreshCw, X, CheckCircle, AlertTriangle } from 'lucide-react';
import { LiveUpdateService, UpdateStatus } from '@/services/LiveUpdateService';
import { Capacitor } from '@capacitor/core';

export function LiveUpdateBanner() {
  const [status, setStatus] = useState<UpdateStatus>(LiveUpdateService.getStatus());
  const [dismissed, setDismissed] = useState(false);
  const autoUpdateTriggered = useRef(false);

  useEffect(() => {
    const unsubscribe = LiveUpdateService.subscribe(setStatus);

    // Check for updates on mount (only on native)
    if (Capacitor.isNativePlatform()) {
      LiveUpdateService.checkForUpdates();
    }

    return unsubscribe;
  }, []);

  // Auto-update para móviles en producción - descarga en segundo plano
  useEffect(() => {
    if (
      Capacitor.isNativePlatform() &&
      status.available &&
      status.newVersion &&
      !status.downloading &&
      !autoUpdateTriggered.current
    ) {
      // Auto-descargar actualización en segundo plano
      autoUpdateTriggered.current = true;
      
      // Pequeño delay para no bloquear el inicio de la app
      setTimeout(async () => {
        const success = await LiveUpdateService.downloadAndApplyUpdate();
        if (success) {
          // Mostrar mensaje breve y recargar
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      }, 3000);
    }
  }, [status.available, status.newVersion, status.downloading]);

  const handleUpdate = async () => {
    const success = await LiveUpdateService.downloadAndApplyUpdate();
    if (success) {
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  // Don't show on web (PWA handles its own updates)
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  // Show error state
  if (status.error) {
    return (
      <Alert variant="destructive" className="m-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>{status.error}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => LiveUpdateService.clearError()}
          >
            <X className="h-4 w-4" />
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Show downloading state
  if (status.downloading) {
    return (
      <Alert className="m-4 border-blue-500 bg-blue-500/10">
        <Download className="h-4 w-4 animate-bounce text-blue-500" />
        <AlertDescription>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span>Actualizando en segundo plano...</span>
              <span className="text-sm text-muted-foreground">{status.progress}%</span>
            </div>
            <Progress value={status.progress} className="h-2" />
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Show success state
  if (status.progress === 100) {
    return (
      <Alert className="m-4 border-green-500 bg-green-500/10">
        <CheckCircle className="h-4 w-4 text-green-500" />
        <AlertDescription className="flex items-center gap-2">
          <span>Actualización aplicada. Reiniciando...</span>
          <RefreshCw className="h-4 w-4 animate-spin" />
        </AlertDescription>
      </Alert>
    );
  }

  // En móvil, la actualización es automática - solo mostrar si hay error o forzamos manual
  // Normalmente no mostramos nada porque ya se inició auto-update
  if (status.available && status.newVersion && !autoUpdateTriggered.current) {
    return (
      <Alert className="m-4 border-primary bg-primary/10">
        <Download className="h-4 w-4 text-primary" />
        <AlertDescription>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium">
                Nueva versión: v{status.newVersion.version}
              </p>
              {status.newVersion.releaseNotes && (
                <p className="text-sm text-muted-foreground">
                  {status.newVersion.releaseNotes}
                </p>
              )}
            </div>
            <Button size="sm" onClick={handleUpdate}>
              <Download className="h-4 w-4 mr-1" />
              Actualizar
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}
